/**
 * Design system colors.
 * https://zeroheight.com/1e5da6515/p/002f3f-colors
 */

import type { ColorString } from 'types';

// Define the full palette for easy reference and usage, any unused colors will
// be removed by minification at build time.
/* oxlint-disable typescript/no-unused-vars */

const black = '#111' as ColorString;
const white = '#fff' as ColorString;
const blue50 = '#f3f6fa' as ColorString;
const blue100 = '#eaeef5' as ColorString;
const blue200 = '#d8e0ed' as ColorString;
const blue300 = '#c0cce1' as ColorString;
const blue400 = '#a6b2d3' as ColorString;
const blue500 = '#8f9ac5' as ColorString;
const blue600 = '#777fb4' as ColorString;
const blue700 = '#686e9f' as ColorString;
const blue800 = '#535880' as ColorString;
const blue900 = '#484d67' as ColorString;
const blue950 = '#2a2c3c' as ColorString;
const emerald50 = '#ecfff5' as ColorString;
const emerald100 = '#d3ffe8' as ColorString;
const emerald200 = '#aaffd2' as ColorString;
const emerald300 = '#69ffb1' as ColorString;
const emerald400 = '#21ff88' as ColorString;
const emerald500 = '#00f267' as ColorString;
const emerald600 = '#00ca51' as ColorString;
const emerald700 = '#009e43' as ColorString;
const emerald800 = '#00853e' as ColorString;
const emerald900 = '#026532' as ColorString;
const emerald950 = '#003919' as ColorString;
const green50 = '#f1faeb' as ColorString;
const green100 = '#dff4d3' as ColorString;
const green200 = '#c1eaac' as ColorString;
const green300 = '#9bdb7b' as ColorString;
const green400 = '#77c952' as ColorString;
const green500 = '#509e2f' as ColorString;
const green600 = '#428a26' as ColorString;
const green700 = '#356a21' as ColorString;
const green800 = '#2c551f' as ColorString;
const green900 = '#28491e' as ColorString;
const green950 = '#12270c' as ColorString;
const grey50 = '#f6f6f6' as ColorString;
const grey100 = '#e7e7e7' as ColorString;
const grey200 = '#d1d1d1' as ColorString;
const grey300 = '#b0b0b0' as ColorString;
const grey400 = '#888888' as ColorString;
const grey500 = '#707070' as ColorString;
const grey600 = '#5d5d5d' as ColorString;
const grey700 = '#4f4f4f' as ColorString;
const grey800 = '#454545' as ColorString;
const grey900 = '#3d3d3d' as ColorString;
const grey950 = '#262626' as ColorString;
const lime50 = '#fffee4' as ColorString;
const lime100 = '#fffec5' as ColorString;
const lime200 = '#feff92' as ColorString;
const lime300 = '#f9ff53' as ColorString;
const lime400 = '#ecfb20' as ColorString;
const lime500 = '#c4d600' as ColorString;
const lime600 = '#a0b500' as ColorString;
const lime700 = '#798902' as ColorString;
const lime800 = '#5f6c08' as ColorString;
const lime900 = '#505b0c' as ColorString;
const lime950 = '#2a3300' as ColorString;
const orange50 = '#fffaeb' as ColorString;
const orange100 = '#fff2c6' as ColorString;
const orange200 = '#ffe388' as ColorString;
const orange300 = '#ffcf4a' as ColorString;
const orange400 = '#ffb81c' as ColorString;
const orange500 = '#ff9d0e' as ColorString;
const orange600 = '#ff8200' as ColorString;
const orange700 = '#cc5f02' as ColorString;
const orange800 = '#a1490b' as ColorString;
const orange900 = '#823e0c' as ColorString;
const orange950 = '#461d04' as ColorString;
const purple50 = '#fcf5fe' as ColorString;
const purple100 = '#f8e9fe' as ColorString;
const purple200 = '#f1d2fc' as ColorString;
const purple300 = '#e9aff8' as ColorString;
const purple400 = '#dd7ff3' as ColorString;
const purple500 = '#ca4ee7' as ColorString;
const purple600 = '#b12ecb' as ColorString;
const purple700 = '#9423a8' as ColorString;
const purple800 = '#7b1f89' as ColorString;
const purple900 = '#6d2077' as ColorString;
const purple950 = '#43084a' as ColorString;
const red50 = '#fdf5f3' as ColorString;
const red100 = '#fce9e7' as ColorString;
const red200 = '#f8d5d3' as ColorString;
const red300 = '#f3b1ae' as ColorString;
const red400 = '#ec8280' as ColorString;
const red500 = '#e05353' as ColorString;
const red600 = '#cb333b' as ColorString;
const red700 = '#ab2530' as ColorString;
const red800 = '#8f222e' as ColorString;
const red900 = '#7b202d' as ColorString;
const red950 = '#440d13' as ColorString;
const sky50 = '#f1f9fe' as ColorString;
const sky100 = '#e3f1fb' as ColorString;
const sky200 = '#c0e5f7' as ColorString;
const sky300 = '#88d1f1' as ColorString;
const sky400 = '#41b6e6' as ColorString;
const sky500 = '#2197d7' as ColorString;
const sky600 = '#0077c8' as ColorString;
const sky700 = '#015ea3' as ColorString;
const sky800 = '#065186' as ColorString;
const sky900 = '#0b446f' as ColorString;
const sky950 = '#072b4a' as ColorString;
const yellow50 = '#feffe7' as ColorString;
const yellow100 = '#faffc1' as ColorString;
const yellow200 = '#faff86' as ColorString;
const yellow300 = '#ffff41' as ColorString;
const yellow400 = '#fff30d' as ColorString;
const yellow500 = '#ffe500' as ColorString;
const yellow600 = '#d1aa00' as ColorString;
const yellow700 = '#a67a02' as ColorString;
const yellow800 = '#895f0a' as ColorString;
const yellow900 = '#744d0f' as ColorString;
const yellow950 = '#442904' as ColorString;

const FOREGROUND_FEEDBACK_COLORS = {
	error: red800,
	information: sky800,
	warning: orange800,
	success: emerald900,
	successHover: '#05542B',
	successActive: '#064C28',
	summary: purple950,
	disabled: grey600,
};

export const KEYWORD_COLORS = {
	current: 'currentColor',
	inherit: 'inherit',
	transparent: 'transparent',
	initial: 'initial',
	revert: 'revert',
	revertLayer: 'revert-layer',
	unset: 'unset',
};

// Globally used colors, for instance accent, fill, stroke, etc.
export const COLORS = {
	...KEYWORD_COLORS,
	...FOREGROUND_FEEDBACK_COLORS,
};

export const BACKGROUND_COLORS = {
	...KEYWORD_COLORS,

	base: white,
	baseHover: '#cfcfcf',
	baseActive: '#b8b8b8',
	inverse: grey950,
	positive: grey950,
	positiveHover: black,
	negative: white,
	negativeHover: grey100,
	strong: grey700,
	subtle: grey100,
	subtleHover: '#bcbcbc',
	subtleActive: '#a7a7a7',
	brand: red600,
	brandHover: '#a52c32',
	brandActive: '#93292e',
	brandDecorative: red600,
	action: emerald900,
	actionHover: '#05542b',
	actionActive: '#064c28',
	shadow: black,
	badge: emerald900,
	badgeError: red800,
	saving: red700,
	opacityLighten: '#ffffff33',
	opacityDarken: '#11111133',

	// Feedback
	error: red100,
	information: sky100,
	warning: orange100,
	success: green100,
	summary: purple50,
	summaryStrong: purple200,
	disabled: grey200,

	// Price tags
	pricetagCampaign: yellow500,
	pricetagJulaclub: red800,
	pricetagJulapro: grey950,

	// Themes
	news: orange400,
	campaign: red600,
	campaignSummer: '#fcdbc1',
	sale: yellow500,
	saleHighlight: red600,
	julaclub: red800,
	julaclubSubtle: red100,
	julapro: grey950,
	blackFriday: '#000',
	blackFridayHighlight: orange500,
	blackWeek: '#000',
	blackWeekHighlight: '#e51a28',
	crazyDeals: '#b70005',
	crazyDealsStop1: '#fcf581',
	crazyDealsStop2: '#d9610a',

	// Internal/custom
	tableStripe: grey50,
	shimmer: grey100,
	shimmerHighlight: grey50,
};

export const ICON_COLORS = {
	...KEYWORD_COLORS,

	base: grey950,
	inverse: white,
	negative: white,
};

export const TEXT_COLORS = {
	...ICON_COLORS,
	...KEYWORD_COLORS,
	...FOREGROUND_FEEDBACK_COLORS,

	base: grey950,
	subtle: grey700,
	inverse: white,
	negative: white,
	positive: grey950,
	brand: red600,
	reducedPrice: red600,
	pricetagCampaign: grey950,
	pricetagJulaclub: white,
	pricetagJulapro: white,
	pricetagSaving: white,

	// Themes
	// Excluding highlight colors that match the base colors since it's pointless
	// to set. It also clashes e.g. with news vs. campaign, where news highlight
	// could always be set (without visible difference) while campaign highlight
	// should only be set for the campaign highlight theme, not the base campaign
	// theme.
	news: grey950,
	// newsHighlight: grey950,
	campaign: white,
	campaignHighlight: yellow500,
	campaignSummer: grey950,
	campaignSummerHighlight: red600,
	sale: grey950,
	saleHighlight: red600,
	blackFriday: white,
	blackFridayHighlight: orange500,
	blackWeek: white,
	blackWeekHighlight: '#e51a28',
	crazyDeals: white,
	julaClub: white,
	// julaClubHighlight: white,
	julaPro: white,
	// julaProHighlight: white,
};

export const SHADOW_COLORS = {
	base: BACKGROUND_COLORS.shadow,
};

export const BORDER_COLORS = {
	...KEYWORD_COLORS,
	...FOREGROUND_FEEDBACK_COLORS,

	subtle: grey200,
	strong: grey700,
	focus: grey950,
	brand: red600,
	brandHover: '#a52c32',
	brandActive: '#93292e',
	brandDecorative: red700,
	inverse: white,
	opacityLighten: '#ffffff33',
};

// Shouldn't be needed in code since they can be changed by editors.
export const CATEGORY_COLORS = {
	toolsMachines: sky400,
	constructionPaint: sky600,
	electricsLightning: orange400,
	clothesSafety: orange600,
	garden: lime500,
	leisure: green500,
	autoGarage: blue700,
	homeHousehold: purple900,
	spareParts: grey500,
	outlet: yellow500,
};

export type BorderColorName = keyof typeof BORDER_COLORS;
export type BackgroundColorName = keyof typeof BACKGROUND_COLORS;

function mapColors<
	ColorSet extends Record<string, string>,
	ColorSetKey extends keyof ColorSet,
>(colorSet: ColorSet, mapper: (key: ColorSetKey) => string) {
	const result = {} as Record<ColorSetKey, string>;
	const keys = Object.keys(colorSet) as ColorSetKey[];
	keys.forEach((key) => {
		result[key] = mapper(key);
	});
	return result;
}

export const BACKGROUND_COLOR_CLASSES = mapColors(
	BACKGROUND_COLORS,
	(key) => `bg-${key}`,
);

export const TEXT_COLOR_CLASSES = mapColors(
	TEXT_COLORS,
	(key) => `text-${key}`,
);
